import type { Asset } from '#modules/AssetOverview/AssetOverview.types'
import type { FunctionComponent } from 'react'

import { FlowPage } from '@monthio/design-system'
import { LoadingScreen, notificationService } from '@monthio/react-components'
import { budgetApiClient } from '#api/budgetApiClient'
import { overarchingTypeOf } from '#modules/AssetOverview/AssetOverview.types'
import { AssetCard } from '#modules/AssetOverview/Components/AssetCard'
import { LoanType } from '#modules/Questionnaire/types/questionnaire.types'
import { Formik } from 'formik'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'

import { ItemWithOptionalLogo } from '../../modulesTemp/shared/ItemWithOptionalLogo'
import { useAssetReplacementModule } from './useAssetReplacementModule'

const AssetsWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
`

export const AssetReplacement: FunctionComponent = () => {
	const { t } = useTranslation()
	const history = useHistory()
	const [assets, setAssets] = useState<Asset[]>([])
	const [assetsToReplace, setAssetsToReplace] = useState<Asset[]>([])
	const [isFetchingData, setIsFetchingData] = useState(false)
	const [hasFetchingDataFailed, setHasFetchingDataFailed] = useState(false)
	const [isReplacingAsset, setIsReplacingAsset] = useState(true)

	const { config, finish } = useAssetReplacementModule()

	const finishModule = useCallback(() => {
		finish()
	}, [history, finish])

	useEffect(() => {
		if (config.clientInputsType === undefined) {
			return
		}

		const fetchData = async () => {
			setIsFetchingData(true)
			const assetsData = await budgetApiClient.fetchAssets()

			if (assetsData.length == 0) {
				finishModule()
			}

			setAssets(assetsData)
		}

		fetchData()
			.catch(() => setHasFetchingDataFailed(true))
			.finally(() => setIsFetchingData(false))
	}, [finishModule, config.clientInputsType])

	useEffect(() => {
		const assetType =
			config.clientInputsType === LoanType.HomeLoan ? 'realEstate' : 'vehicle'
		const _assetsToReplace = assets.filter(
			(x) => overarchingTypeOf(x.assetType) === assetType,
		)
		setAssetsToReplace(_assetsToReplace)
	}, [assets, config.clientInputsType])

	const handleSubmit = async (_assetsToReplace: Asset[]) => {
		if (isReplacingAsset) {
			const excludedFromFutureBudgetIds = _assetsToReplace
				.filter((x) => x.excludedFromFutureBudget)
				.map((x) => x.id)

			const assetsToSend = assets.map((asset) => ({
				...asset,
				excludedFromFutureBudget: excludedFromFutureBudgetIds.includes(
					asset.id,
				),
			}))

			try {
				await budgetApiClient.updateAssets(assetsToSend)
			} catch {
				notificationService.error(
					t('assetReplacementPage-submitEstimates-error'),
				)
			}
		}

		finishModule()
	}

	if (config.clientInputsType == null) {
		finishModule()
	}

	if (hasFetchingDataFailed) {
		return (
			<FlowPage>
				<FlowPage.Content $isCentered>
					<FlowPage.Header title={t('assetReplacementPage-failure-text')} />
				</FlowPage.Content>
			</FlowPage>
		)
	}

	if (isFetchingData) {
		return (
			<FlowPage>
				<FlowPage.Content $isCentered>
					<LoadingScreen />
				</FlowPage.Content>
			</FlowPage>
		)
	}

	return (
		<Formik
			initialValues={{ assets: assetsToReplace }}
			onSubmit={(values) => handleSubmit(values.assets)}
		>
			{({ values, submitForm, setFieldValue }) => {
				return (
					<FlowPage>
						<FlowPage.Content $spacing="xl">
							<FlowPage.Header
								title={t('assetReplacementPage-title')}
								description={t('assetReplacementPage-subtitle')}
							/>
							<AssetsWrapper>
								{values.assets.map((asset, index) => (
									<AssetCard
										key={asset.id}
										asset={asset}
										hasBorder
										isSelected={asset.excludedFromFutureBudget}
										onSelect={(isSelected) => {
											if (isSelected) {
												setIsReplacingAsset(true)
											}
											setFieldValue(
												`assets[${index}].excludedFromFutureBudget`,
												isSelected,
											)
										}}
										variant="preview"
									/>
								))}
								<ItemWithOptionalLogo
									title={t('assetReplacementPage-isReplacingCheckbox-label')}
									isRounded
									hasBorder
									isSelected={!isReplacingAsset}
									onItemSelect={(selected) => {
										values.assets.forEach((_value, index) =>
											setFieldValue(
												`assets[${index}].excludedFromFutureBudget`,
												false,
											),
										)
										setIsReplacingAsset(!selected)
									}}
								/>
							</AssetsWrapper>
						</FlowPage.Content>
						<FlowPage.Footer
							actions={
								<FlowPage.Cta onClick={submitForm}>
									{t('assetReplacementPage-continue-button')}
								</FlowPage.Cta>
							}
						/>
					</FlowPage>
				)
			}}
		</Formik>
	)
}
